// src/components/ReAirClassModal.js
// Admin-only: put an already-recorded past class back on the Schedule.
//
// Two use cases drove this: a week where no new class can be recorded (better to
// re-air a strong one than have nothing), and running back a class that got high
// engagement.
//
// The modal is presentational + one write. It gathers the schedule details and
// calls createReAirClass(), which creates a standard pre-recorded class pointing
// at the EXISTING Mux asset — no upload, no new recording, and no second On Demand
// entry (classRecordings are keyed by muxAssetId and the cron skips creation when
// one already exists). See services/liveClasses.js#createReAirClass.
//
// Flow: pick a past class (skipped when opened from a specific card) → set the
// date/time and confirm the details → schedule.
//
// All sub-components are module-scope (never defined inside the parent) so a
// keystroke can't remount them and steal input focus.

import React, { useState, useMemo } from 'react';
import { createReAirClass } from '../services/liveClasses';
import { releaseAnchorIso, formatReleaseDate } from '../services/classRelease';

// ── Date helpers ───────────────────────────────────────────────────────────
// Mirrors the create-class form exactly: a datetime-local value is read as local
// time and stored as an ISO string (new Date(value).toISOString()).
function toLocalInputValue(date) {
    const pad = (n) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

// Default suggestion: tomorrow at 9:00am local — a sensible slot the admin will
// almost always change, but it means the field is never empty on open.
function defaultScheduleValue() {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(9, 0, 0, 0);
    return toLocalInputValue(d);
}

function formatAired(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function minutesLabel(rec) {
    const mins = rec.duration || (rec.durationSeconds ? Math.round(rec.durationSeconds / 60) : 0);
    return mins ? `${mins} min` : '';
}

// ── Engagement line ────────────────────────────────────────────────────────
// Surfaced in the picker so "run back the one that popped" is a decision the
// admin can make right here, without a separate trip to Metrics. Plain type, no
// emoji — matches the premium treatment used elsewhere.
function engagementLabel(rec) {
    const likes = rec.likeCount || 0;
    const comments = rec.commentCount || 0;
    if (!likes && !comments) return '';
    const parts = [];
    if (likes) parts.push(`${likes} ${likes === 1 ? 'heart' : 'hearts'}`);
    if (comments) parts.push(`${comments} ${comments === 1 ? 'comment' : 'comments'}`);
    return parts.join(' · ');
}

const LABEL = {
    fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600,
    color: 'var(--mom-charcoal)', marginBottom: 4, display: 'block',
};

// ── One row in the picker ──────────────────────────────────────────────────
function RecordingRow({ recording, selected, onSelect }) {
    const [hover, setHover] = useState(false);
    const engagement = engagementLabel(recording);
    return (
        <button
            type="button"
            onClick={() => onSelect(recording)}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            style={{
                display: 'flex', gap: 12, alignItems: 'center', width: '100%', textAlign: 'left',
                padding: 10, borderRadius: 12, cursor: 'pointer',
                background: selected
                    ? 'color-mix(in srgb, var(--mom-primary) 10%, transparent)'
                    : (hover ? 'var(--mom-cream)' : 'transparent'),
                border: selected ? '1.5px solid var(--mom-primary)' : '1.5px solid transparent',
                transition: 'background 0.15s ease, border-color 0.15s ease',
            }}
        >
            <div style={{
                width: 88, height: 50, borderRadius: 8, flexShrink: 0, overflow: 'hidden',
                background: 'var(--mom-cream-dark)',
            }}>
                {recording.thumbnailUrl && (
                    <img src={recording.thumbnailUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                )}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                    fontFamily: 'var(--mom-font-sans)', fontSize: 13.5, fontWeight: 600,
                    color: 'var(--mom-charcoal)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>{recording.title || 'Untitled class'}</div>
                <div style={{
                    fontFamily: 'var(--mom-font-sans)', fontSize: 11.5, color: 'var(--mom-warm-gray)', marginTop: 3,
                }}>
                    {[minutesLabel(recording), formatAired(recording.createdAt)].filter(Boolean).join(' · ')}
                </div>
                {engagement && (
                    <div style={{
                        fontFamily: 'var(--mom-font-sans)', fontSize: 11.5,
                        color: 'var(--mom-primary)', marginTop: 2,
                    }}>{engagement}</div>
                )}
            </div>
        </button>
    );
}

function ReAirClassModal({ recordings = [], initialRecording = null, onClose, onScheduled }) {
    const [selected, setSelected] = useState(initialRecording);
    const [search, setSearch] = useState('');
    const [scheduledAt, setScheduledAt] = useState(defaultScheduleValue);
    // Release day (default) vs set a time — same choice as the Schedule a Class
    // form (docs/plans/release-day-classes-plan.md). Release day = every member
    // gets the class at 12:00 AM on that date in her own time zone.
    const [releaseMode, setReleaseMode] = useState('day');
    const [releaseDate, setReleaseDate] = useState(() => defaultScheduleValue().slice(0, 10));
    const [title, setTitle] = useState(initialRecording?.title || '');
    const [description, setDescription] = useState(initialRecording?.description || '');
    const [price, setPrice] = useState(initialRecording?.isFree ? 0 : 1);
    // Recordings don't carry `classic` today (it's a Schedule-only tag), so this
    // starts unchecked; reading it keeps the door open if that ever changes.
    const [classic, setClassic] = useState(!!initialRecording?.classic);
    const [easeIn, setEaseIn] = useState(!!initialRecording?.easeIn);
    const [strongPlus, setStrongPlus] = useState(!!initialRecording?.strongPlus);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    // Only recordings with a live Mux asset can be re-aired.
    const airable = useMemo(() => (
        recordings.filter((r) => r.muxAssetId && r.muxPlaybackId)
    ), [recordings]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return airable;
        return airable.filter((r) => (r.title || '').toLowerCase().includes(q));
    }, [airable, search]);

    const choose = (rec) => {
        setSelected(rec);
        setTitle(rec.title || '');
        setDescription(rec.description || '');
        setPrice(rec.isFree ? 0 : 1);
        setClassic(!!rec.classic);
        setEaseIn(!!rec.easeIn);
        setStrongPlus(!!rec.strongPlus);
        setError('');
    };

    const whenReady = releaseMode === 'day' ? !!releaseDate : !!scheduledAt;

    const handleSchedule = async () => {
        if (!selected || !whenReady || !title.trim() || saving) return;
        setSaving(true);
        setError('');
        const newId = await createReAirClass(selected, {
            title: title.trim(),
            description: description.trim(),
            ...(releaseMode === 'day'
                ? { scheduledAt: releaseAnchorIso(releaseDate), releaseMode: 'day', releaseDate }
                : { scheduledAt: new Date(scheduledAt).toISOString() }),
            price: Number(price) || 0,
            classic,
            easeIn,
            strongPlus,
        });
        setSaving(false);
        if (!newId) {
            setError("That class couldn't be scheduled. Please try again.");
            return;
        }
        if (typeof onScheduled === 'function') onScheduled(newId);
    };

    const canSubmit = !!selected && whenReady && !!title.trim() && !saving;

    return (
        <div
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
            style={{
                position: 'fixed', inset: 0, zIndex: 10000,
                background: 'rgba(40,30,30,0.45)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
                animation: 'fadeIn 0.15s ease-out',
            }}
        >
            <div style={{
                background: 'var(--mom-white)', borderRadius: 20, width: '100%', maxWidth: 640,
                maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden',
                boxShadow: '0 28px 70px rgba(0,0,0,0.24)', animation: 'modalIn 0.22s ease-out',
            }}>
                {/* Header */}
                <div style={{
                    padding: '18px 22px 14px', borderBottom: '1px solid var(--mom-cream-dark)',
                    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12,
                }}>
                    <div>
                        <h3 style={{
                            fontFamily: 'var(--mom-font-serif)', fontSize: 19, fontStyle: 'italic',
                            fontWeight: 700, color: 'var(--mom-charcoal)',
                        }}>Re-air a past class</h3>
                        <p style={{
                            fontFamily: 'var(--mom-font-sans)', fontSize: 12.5,
                            color: 'var(--mom-warm-gray)', marginTop: 3, lineHeight: 1.5,
                        }}>Put a class you've already recorded back on the schedule.</p>
                    </div>
                    <button onClick={onClose} aria-label="Close" style={{
                        background: 'none', border: 'none', fontSize: 24, lineHeight: 1,
                        cursor: 'pointer', color: 'var(--mom-warm-gray)', padding: 0,
                    }}>×</button>
                </div>

                {/* Body */}
                <div style={{ padding: '16px 22px', overflowY: 'auto', WebkitOverflowScrolling: 'touch', flex: 1 }}>
                    {/* ── Step 1: which class ── */}
                    {!selected ? (
                        <>
                            <label style={LABEL}>Choose a class</label>
                            <input
                                type="text"
                                className="mom-input"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search past classes…"
                                autoFocus
                                style={{ marginBottom: 10 }}
                            />
                            {filtered.length === 0 ? (
                                <p style={{
                                    fontFamily: 'var(--mom-font-sans)', fontSize: 13,
                                    color: 'var(--mom-warm-gray)', padding: '20px 4px', textAlign: 'center', lineHeight: 1.6,
                                }}>
                                    {airable.length === 0
                                        ? 'No past class recordings are available to re-air yet.'
                                        : 'No classes match that search.'}
                                </p>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    {filtered.map((rec) => (
                                        <RecordingRow key={rec.id} recording={rec} selected={false} onSelect={choose} />
                                    ))}
                                </div>
                            )}
                        </>
                    ) : (
                        <>
                            {/* Chosen class — compact summary with a way back to the list */}
                            <div style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                gap: 12, marginBottom: 16,
                            }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <RecordingRow recording={selected} selected onSelect={() => {}} />
                                </div>
                                {!initialRecording && (
                                    <button
                                        onClick={() => setSelected(null)}
                                        style={{
                                            background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                                            fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600,
                                            color: 'var(--mom-link)', flexShrink: 0,
                                        }}
                                    >Change</button>
                                )}
                            </div>

                            {/* ── Step 2: when + details ── */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                <div>
                                    <div style={{ display: 'inline-flex', borderRadius: 10, overflow: 'hidden', border: '1.5px solid var(--mom-cream-dark)', marginBottom: 10 }}>
                                        {[{ key: 'day', label: 'Release day' }, { key: 'time', label: 'Set a time' }].map((opt) => (
                                            <button key={opt.key} type="button" onClick={() => setReleaseMode(opt.key)}
                                                style={{
                                                    padding: '7px 16px', fontSize: 12.5, fontWeight: 600, border: 'none', cursor: 'pointer',
                                                    fontFamily: 'var(--mom-font-sans)',
                                                    background: releaseMode === opt.key ? 'var(--mom-primary)' : 'var(--mom-white)',
                                                    color: releaseMode === opt.key ? 'var(--mom-white)' : 'var(--mom-warm-gray)',
                                                }}>
                                                {opt.label}
                                            </button>
                                        ))}
                                    </div>
                                    {releaseMode === 'day' ? (
                                        <>
                                            <label style={LABEL}>Release day</label>
                                            <input
                                                type="date"
                                                className="mom-input"
                                                value={releaseDate}
                                                onChange={(e) => setReleaseDate(e.target.value)}
                                            />
                                            {releaseDate && (
                                                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontStyle: 'italic', color: 'var(--mom-warm-gray)', margin: '6px 0 0' }}>
                                                    Out {formatReleaseDate(releaseDate)} — first thing, in each member's own time zone.
                                                </p>
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            <label style={LABEL}>Date &amp; time</label>
                                            <input
                                                type="datetime-local"
                                                className="mom-input"
                                                value={scheduledAt}
                                                onChange={(e) => setScheduledAt(e.target.value)}
                                            />
                                        </>
                                    )}
                                </div>
                                <div>
                                    <label style={LABEL}>Title</label>
                                    <input
                                        type="text"
                                        className="mom-input"
                                        value={title}
                                        onChange={(e) => setTitle(e.target.value)}
                                        placeholder="Class title"
                                    />
                                </div>
                                <div>
                                    <label style={LABEL}>Description</label>
                                    <textarea
                                        className="mom-input"
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                        rows={3}
                                        placeholder="What members should expect"
                                        style={{ resize: 'vertical' }}
                                    />
                                </div>
                                <div>
                                    <label style={LABEL}>Access</label>
                                    <select className="mom-input" value={price} onChange={(e) => setPrice(e.target.value)}>
                                        <option value={1}>Subscribers</option>
                                        <option value={0}>Free</option>
                                    </select>
                                </div>
                                <label style={{
                                    display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
                                    fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: 'var(--mom-charcoal)',
                                }}>
                                    <input type="checkbox" checked={classic} onChange={(e) => setClassic(e.target.checked)} />
                                    Classic (signature class)
                                </label>
                                <label style={{
                                    display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
                                    fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: 'var(--mom-charcoal)',
                                }}>
                                    <input type="checkbox" checked={easeIn} onChange={(e) => setEaseIn(e.target.checked)} />
                                    Ease In (beginner friendly)
                                </label>
                                <label style={{
                                    display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
                                    fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: 'var(--mom-charcoal)',
                                }}>
                                    <input type="checkbox" checked={strongPlus} onChange={(e) => setStrongPlus(e.target.checked)} />
                                    Strong+ (heavier lifting)
                                </label>
                            </div>

                            {/* What happens next — states the one thing the admin needs to trust. */}
                            <div style={{
                                marginTop: 16, padding: '12px 14px', borderRadius: 12,
                                background: 'var(--mom-cream)',
                                borderLeft: '3px solid var(--mom-primary)',
                            }}>
                                <p style={{
                                    fontFamily: 'var(--mom-font-sans)', fontSize: 12.5,
                                    color: 'var(--mom-charcoal)', lineHeight: 1.6,
                                }}>
                                    This airs on the schedule like any other class — waiting room, reminders,
                                    live comments and reactions. It <strong>won't be added to On Demand again</strong>;
                                    members will find it right where it already is.
                                </p>
                                <p style={{
                                    fontFamily: 'var(--mom-font-sans)', fontSize: 11.5,
                                    color: 'var(--mom-warm-gray)', lineHeight: 1.6, marginTop: 6,
                                }}>
                                    Muscle groups and audience carry over from the original.
                                </p>
                            </div>

                            {error && (
                                <p style={{
                                    fontFamily: 'var(--mom-font-sans)', fontSize: 13,
                                    color: 'var(--mom-danger, #e53e3e)', marginTop: 12,
                                }}>{error}</p>
                            )}
                        </>
                    )}
                </div>

                {/* Footer */}
                <div style={{
                    padding: '14px 22px 18px', borderTop: '1px solid var(--mom-cream-dark)',
                    display: 'flex', gap: 10, justifyContent: 'flex-end', alignItems: 'center',
                }}>
                    <button
                        className="mom-btn"
                        onClick={onClose}
                        style={{
                            padding: '11px 22px', borderRadius: 12, fontSize: 14, fontWeight: 600,
                            fontFamily: 'var(--mom-font-sans)', background: 'var(--mom-white)',
                            color: 'var(--mom-charcoal)', border: '1px solid var(--mom-cream-dark)', cursor: 'pointer',
                        }}
                    >Cancel</button>
                    <button
                        className="mom-btn mom-btn-primary"
                        onClick={handleSchedule}
                        disabled={!canSubmit}
                        style={{
                            padding: '11px 26px', borderRadius: 12, fontSize: 14, fontWeight: 700,
                            fontFamily: 'var(--mom-font-sans)',
                            opacity: canSubmit ? 1 : 0.5, cursor: canSubmit ? 'pointer' : 'not-allowed',
                        }}
                    >{saving ? 'Scheduling…' : 'Schedule re-air'}</button>
                </div>
            </div>
        </div>
    );
}

export default ReAirClassModal;
